/**
 * Firebase App Check (ADM-14) — proves an API request comes from this portal's genuine
 * web origin, not just from a request holding a stolen/valid JWT. Mirrors the mobile
 * app's approach (app/services/appCheck.ts): best-effort, never fatal in the *dev server*.
 * In a `vite build` bundle (import.meta.env.PROD — every branch this repo deploys via CI,
 * see .github/workflows/deploy.yml, hardcodes the site key for all of main/staging/develop)
 * a missing site key throws immediately at boot instead of silently no-op'ing. This repo has
 * hit "Missing App Check token" 401s at least 3 times (ADM-14, ADM-17, docs/admin-portal.md
 * §17.9) purely because this init failed silently and the resulting missing-header 401 looked
 * unrelated to its cause — fail loud in any built bundle so config drift surfaces at load time
 * instead of downstream as a confusing backend 401.
 *
 * The registered key is a reCAPTCHA ENTERPRISE key (created via `gcloud recaptcha keys
 * create`, registered against the Firebase web app via the App Check
 * `recaptchaEnterpriseConfig` endpoint) — so the client MUST use `ReCaptchaEnterpriseProvider`
 * below, not the classic `ReCaptchaV3Provider`. Using the wrong provider silently 400s the
 * token exchange (appCheck/initial-throttle in the console) and the app never sends a
 * header at all, even with a valid site key configured end-to-end.
 */
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
  getToken,
  type AppCheck,
} from 'firebase/app-check';
import { firebaseApp } from '../config/firebase';
import { env } from '../config/env';

let appCheck: AppCheck | null = null;
let unavailable = false;

export function initAppCheckOnce(): void {
  if (appCheck || unavailable) return;
  if (!env.recaptchaV3SiteKey) {
    if (import.meta.env.PROD) {
      throw new Error(
        '[appCheck] VITE_RECAPTCHA_V3_SITE_KEY is unset in a production build — requests ' +
          'would silently omit the X-Firebase-AppCheck header and get rejected with ' +
          '"Missing App Check token". Set it in .github/workflows/deploy.yml (see ADM-14).',
      );
    }
    unavailable = true;
    console.warn('[appCheck] no site key configured — running without App Check (dev server only).');
    return;
  }
  try {
    appCheck = initializeAppCheck(firebaseApp, {
      provider: new ReCaptchaEnterpriseProvider(env.recaptchaV3SiteKey),
      isTokenAutoRefreshEnabled: true,
    });
  } catch (error) {
    unavailable = true;
    console.warn('[appCheck] initialization failed — App Check disabled:', error);
  }
}

export async function getAppCheckToken(): Promise<string | null> {
  if (unavailable) return null;
  if (!appCheck) initAppCheckOnce();
  if (!appCheck) return null;
  try {
    const result = await getToken(appCheck, false);
    return result?.token ?? null;
  } catch (error) {
    console.warn('[appCheck] getToken failed:', error);
    return null;
  }
}
